import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { uniqueId } from "lodash";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";

import { actions as topikActions } from "../../../../modules/topik";
import CharCounter from "../../../../components/form/CharCounter";
import { commitHaptic } from "../haptics";
import AnalysisFrame from "./AnalysisFrame";
import AnalysisRecall, { chosenPairCards } from "./AnalysisRecall";
import TopikAssistPanel from "./TopikAssistPanel";

// Every issue field is named by the question above the list.
const ItemList = ({ t, items, maxLength, labelledBy, onChange }) => {
  const inputRefs = useRef([]);
  const focusIndex = useRef(null);

  const add = () => {
    focusIndex.current = items.length;
    onChange([...items, ""]);
  };
  const update = (i, val) => onChange(items.map((v, idx) => (idx === i ? val : v)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  useEffect(() => {
    if (focusIndex.current !== null) {
      const input = inputRefs.current[focusIndex.current];
      if (input) input.focus();
      focusIndex.current = null;
    }
  }, [items.length]);

  return (
    <div className="topik-analysis__item-list">
      {items.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="topik-analysis__item-row">
          <input
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            type="text"
            className="topik-analysis__item-input"
            value={item}
            maxLength={maxLength}
            placeholder={t("topik-issues-view.item-placeholder")}
            aria-labelledby={labelledBy}
            onChange={(e) => update(i, e.target.value)}
          />
          <CharCounter value={item} maxLength={maxLength} className="char-counter--inline" />
          <button
            type="button"
            className="topik-analysis__item-remove"
            onClick={() => remove(i)}
            aria-label={t("topik-issues-view.remove-item")}
          >
            ×
          </button>
        </div>
      ))}
      <button type="button" className="topik-analysis__add-button" onClick={add}>
        <span aria-hidden="true">+</span> {t("topik-issues-view.add-item")}
      </button>
    </div>
  );
};

ItemList.propTypes = {
  t: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  maxLength: PropTypes.number.isRequired,
  labelledBy: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

ItemList.defaultProps = {
  labelledBy: undefined,
};

const TopikIssuesView = ({ t, session, topikStatus, gameClosed, dispatch }) => {
  const [items, setItems] = useState(
    session.issues && session.issues.length ? session.issues : [""],
  );
  const filled = items.filter((v) => v.trim().length > 0);
  const questionId = useRef(uniqueId("topik-issues-question-")).current;

  const handleSubmit = () => {
    if (filled.length === 0 || topikStatus.submitting || gameClosed) return;
    commitHaptic();
    dispatch(topikActions.analysis.issues.request({ issues: filled }));
  };

  return (
    <AnalysisFrame
      t={t}
      title={t("topik-issues-view.title")}
      canBack
      submitting={topikStatus.submitting}
      gameClosed={gameClosed}
      error={topikStatus.error}
      onBack={() => dispatch(topikActions.analysis.back.request())}
    >
      <div className="topik-analysis__step">
        {/* The question asks for issues tied to "your observation" - show it back to the player. */}
        <AnalysisRecall
          entries={[{
            key: "observation",
            label: t("topik-analysis.recall.observation"),
            value: session.observation,
            cards: chosenPairCards(session),
          }]}
        />
        <p id={questionId} className="topik-analysis__question">{t("topik-issues-view.question")}</p>
        {/* E6: honour a top-of-scale seriousness rating explicitly. */}
        {session.seriousness === 5 && (
          <p className="topik-analysis__seriousness-five">{t("topik-issues-view.seriousness-5")}</p>
        )}
        {/* E7: permission line - one issue is a complete answer. */}
        <p className="topik-analysis__permission">{t("topik-issues-view.permission")}</p>
        <ItemList
          t={t}
          items={items}
          maxLength={session.maxTextLength}
          labelledBy={questionId}
          onChange={setItems}
        />
        {/* The graduated AI companion (§6.5); the filled items travel as the draft. */}
        <TopikAssistPanel step="issues" draft={filled.join("; ")} />
        <button
          type="button"
          className="topik-view__submit-button"
          disabled={filled.length === 0 || topikStatus.submitting || gameClosed}
          onClick={handleSubmit}
        >
          {t("topik-issues-view.submit")}
        </button>
      </div>
    </AnalysisFrame>
  );
};

TopikIssuesView.propTypes = {
  t: PropTypes.func.isRequired,
  session: PropTypes.object.isRequired,
  topikStatus: PropTypes.object.isRequired,
  gameClosed: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
};

TopikIssuesView.defaultProps = {
  gameClosed: false,
};

const mapStateToProps = (state) => ({
  session: state.topik.session,
  topikStatus: state.topik.status,
});

export default compose(withTranslation("views"), connect(mapStateToProps))(TopikIssuesView);
